import moment from 'moment';
import { Component, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
import { FacsTemp, FacsChanger } from '../../core/constants/facs-const';
import { WindowScrollService } from '../../core/services/window-scroll.service';
import { FormGroup, ControlContainer } from '@angular/forms';

interface RangeModel {
  field: string;
  from?: string;
  to?: string;
}

@Component({
  selector: 'app-facs-filters',
  templateUrl: './facs-filters.component.html',
  styleUrls: ['./facs-filters.component.scss']
})

export class FacsFiltersComponent implements OnInit, OnDestroy {
  public filterForm: FormGroup;
  public listChanger = FacsChanger;
  public listTemperature = FacsTemp;
  public createTime = {
    field: 'creation_date',
    date: {
      from: null,
      to: null,
    },
    hour: {
      from: '00:00',
      to: '23:59',
    }
  };
  public updateTime = {
    field: 'last_update_date',
    date: {
      from: null,
      to: null,
    },
    hour: {
      from: '00:00',
      to: '23:59',
    }
  };
  public distributionTime = {
    field: 'distribution_date',
    date: {
      from: null,
      to: null,
    },
    hour: {
      from: '00:00',
      to: '23:59',
    }
  };

  @Output() closeModal: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitFilter: EventEmitter<void> = new EventEmitter<void>();
  @Output() resetClick: EventEmitter<void> = new EventEmitter<void>();

  disabledUpdateStartDate = (startValue: Date): boolean => {
    if (!startValue || !this.updateTime.date.to) {
      return false;
    }
    return (startValue.getTime()) > this.updateTime.date.to.getTime();
  }

  disabledUpdateFinalDate = (endValue: Date): boolean => {
    if (!endValue || !this.updateTime.date.from) {
      return false;
    }
    return (endValue.getTime() + 86400000) <= this.updateTime.date.from.getTime();
  }

  disabledCreateStartDate = (startValue: Date): boolean => {
    if (!startValue || !this.createTime.date.to) {
      return false;
    }
    return (startValue.getTime()) > this.createTime.date.to.getTime();
  }

  disabledCreateEndDate = (endValue: Date): boolean => {
    if (!endValue || !this.createTime.date.from) {
      return false;
    }
    return (endValue.getTime() + 86400000) <= this.createTime.date.from.getTime();
  }

  disabledDistributionStartDate = (startValue: Date): boolean => {
    if (!startValue || !this.distributionTime.date.to) {
      return false;
    }
    return (startValue.getTime()) > this.distributionTime.date.to.getTime();
  }

  disabledDistributionEndDate = (endValue: Date): boolean => {
    if (!endValue || !this.distributionTime.date.from) {
      return false;
    }
    return (endValue.getTime() + 86400000) <= this.distributionTime.date.from.getTime();
  }

  constructor(
    private windowService: WindowScrollService,
    private controlContainer: ControlContainer
  ) { }

  ngOnInit() {
    this.filterForm = this.controlContainer.control as FormGroup;
    this.populateDate();
    this.windowService.disable();
  }

  ngOnDestroy() {
    this.windowService.enable();
  }

  populateDate() {
    const index = 10; // Zulu time T index Ex: '2020-07-23T23:59:59.999Z'

    if (this.filterForm.get('createTime').value) {
      if (this.filterForm.get('createTime').value.from) {
        this.createTime.date.from = moment(this.filterForm.get('createTime').value.from.substr(0, index)).utc().toDate();
        this.createTime.hour.from = this.filterForm.get('createTime').value.from.substr(index + 1, 5);
      }

      if (this.filterForm.get('createTime').value.to) {
        this.createTime.date.to = moment(this.filterForm.get('createTime').value.to.substr(0, index)).utc().toDate();
        this.createTime.hour.to = this.filterForm.get('createTime').value.to.substr(index + 1, 5);
      }
    }

    if (this.filterForm.get('updateTime').value) {
      if (this.filterForm.get('updateTime').value.from) {
        this.updateTime.date.from = moment(this.filterForm.get('updateTime').value.from.substr(0, index)).utc().toDate();
        this.updateTime.hour.from = this.filterForm.get('updateTime').value.from.substr(index + 1, 5);
      }

      if (this.filterForm.get('updateTime').value.to) {
        this.updateTime.date.to = moment(this.filterForm.get('updateTime').value.to.substr(0, index)).utc().toDate();
        this.updateTime.hour.to = this.filterForm.get('updateTime').value.to.substr(index + 1, 5);
      }
    }

    if (this.filterForm.get('distributionTime').value) {
      if (this.filterForm.get('distributionTime').value.from) {
        this.distributionTime.date.from = moment(this.filterForm.get('distributionTime').value.from.substr(0, index)).utc().toDate();
        this.distributionTime.hour.from = this.filterForm.get('distributionTime').value.from.substr(index + 1, 5);
      }

      if (this.filterForm.get('distributionTime').value.to) {
        this.distributionTime.date.to = moment(this.filterForm.get('distributionTime').value.to.substr(0, index)).utc().toDate();
        this.distributionTime.hour.to = this.filterForm.get('distributionTime').value.to.substr(index + 1, 5);
      }
    }
  }

  clickClose() {
    this.closeModal.emit(false);
  }

  selectIncorporator() {
    if (this.filterForm.get('notCorporator').value === true) {
      this.filterForm.get('corporator').patchValue(true);
      this.filterForm.get('notCorporator').patchValue(false);
    }
  }

  selectNotIncorporator() {
    if (this.filterForm.get('corporator').value === true) {
      this.filterForm.get('notCorporator').patchValue(true);
      this.filterForm.get('corporator').patchValue(false);
    }
  }

  resetForm() {
    this.createTime = {
      field: 'creation_date',
      date: {
        from: null,
        to: null,
      },
      hour: {
        from: '',
        to: '',
      }
    };
    this.updateTime = {
      field: 'last_update_date',
      date: {
        from: null,
        to: null,
      },
      hour: {
        from: '',
        to: '',
      }
    };
    this.distributionTime = {
      field: 'distribution_date',
      date: {
        from: null,
        to: null,
      },
      hour: {
        from: '00:00',
        to: '23:59',
      }
    };
    this.filterForm.reset();
    this.resetClick.emit();
  }

  sendFilter() {
    this.formatDate();
    this.emitFilter.emit();
  }

  formatDate() {
    const formatString = (date, hour, from = true): string => {
      if (!date || !hour) {
        return null;
      }
      return moment(new Date(date).setHours(0, 0, 0, 0)).format(`YYYY-MM-DD[T]${hour}${from ? ':00.000[Z]' : ':59.999[Z]'}`);
    };

    const formatObject = (fieldName, fromDate, fromHour, toDate, toHour): RangeModel => {
      const resp: RangeModel = {
        field: fieldName,
        from: formatString(fromDate, fromHour, true),
        to: formatString(toDate, toHour, false)
      };

      if (!fromDate || !fromHour) {
        delete resp.from;
      }
      if (!toDate || !toHour) {
        delete resp.to;
      }
      if (!resp.from && !resp.to) {
        return null;
      }

      return resp;
    };

    this.filterForm.get('createTime').patchValue(
      formatObject(this.createTime.field, this.createTime.date.from,
        this.createTime.hour.from, this.createTime.date.to, this.createTime.hour.to)
    );

    this.filterForm.get('updateTime').patchValue(
      formatObject(this.updateTime.field, this.updateTime.date.from,
        this.updateTime.hour.from, this.updateTime.date.to, this.updateTime.hour.to)
    );

    this.filterForm.get('distributionTime').patchValue(
      formatObject(this.distributionTime.field, this.distributionTime.date.from,
        this.distributionTime.hour.from, this.distributionTime.date.to, this.distributionTime.hour.to)
    );
  }
}
